import { useQuery, gql, MutationHookOptions, useMutation, QueryHookOptions } from '@apollo/client';
import { Exception } from '@jewelutions/core-common-domain';
import {
  GQLJobSummary,
  GQLQueryWebshop_ScheduleWebshopPublishingJobsArgs,
  GQLQueryWebshop_RetrieveMerchantOrdersFromWebshopJobsArgs,
  GQLMutationWebshop_RetrieveMerchantOrdersFromWebshopArgs,
  GQLMutationWebshop_ScheduleWebshopPublishingArgs,
  GQLMerchantChannelSynchronisedWithShopStatus,
  GQLQueryWebshop_GetMerchantChannelSynchronisedWithShopStatusArgs
} from '@jewelutions/core-crm-server-api-client-domain';
import { JOB_SUMMARY_FIELDS } from './fragments/job';
import { MERCHANT_CHANNEL_SYNCHRONISED_WITH_SHOP_STATUS_FIELDS } from './fragments/merchant';

export class WebshopOperation {
  jobSummaryArrayFromResponse(data: any): GQLJobSummary[] {
    if (data.Webshop_scheduleWebshopPublishingJobs !== undefined) {
      return data.Webshop_scheduleWebshopPublishingJobs;
    }
    if (data.Webshop_retrieveMerchantOrdersFromWebshopJobs !== undefined) {
      return data.Webshop_retrieveMerchantOrdersFromWebshopJobs;
    }
    throw new Exception(`Cannot handle input data jobSummaryArrayFromResponse`);
  }

  scheduleWebshopPublishingJobs(args: GQLQueryWebshop_ScheduleWebshopPublishingJobsArgs) {
    const query = gql`
      ${JOB_SUMMARY_FIELDS}
      query Webshop_scheduleWebshopPublishingJobs($merchantChannelId: ID!) {
        Webshop_scheduleWebshopPublishingJobs(merchantChannelId: $merchantChannelId) {
          ...JobSummaryFields
        }
      }
    `;
    return useQuery<GQLJobSummary[], GQLQueryWebshop_ScheduleWebshopPublishingJobsArgs>(query, {
      variables: args
    });
  }

  retrieveMerchantOrdersFromWebshopJobs(args: GQLQueryWebshop_RetrieveMerchantOrdersFromWebshopJobsArgs) {
    const query = gql`
      ${JOB_SUMMARY_FIELDS}
      query Webshop_retrieveMerchantOrdersFromWebshopJobs($merchantChannelId: ID!) {
        Webshop_retrieveMerchantOrdersFromWebshopJobs(merchantChannelId: $merchantChannelId) {
          ...JobSummaryFields
        }
      }
    `;
    return useQuery<GQLJobSummary[], GQLQueryWebshop_RetrieveMerchantOrdersFromWebshopJobsArgs>(query, {
      variables: args
    });
  }

  retrieveMerchantOrdersFromWebshop(
    options: MutationHookOptions<Boolean, GQLMutationWebshop_RetrieveMerchantOrdersFromWebshopArgs> = {}
  ) {
    const mutation = gql`
      mutation Webshop_retrieveMerchantOrdersFromWebshop($merchantChannelId: ID!) {
        Webshop_retrieveMerchantOrdersFromWebshop(merchantChannelId: $merchantChannelId)
      }
    `;
    return useMutation<Boolean, GQLMutationWebshop_RetrieveMerchantOrdersFromWebshopArgs>(mutation, options);
  }

  scheduleWebshopPublishing(
    options: MutationHookOptions<Boolean, GQLMutationWebshop_ScheduleWebshopPublishingArgs> = {}
  ) {
    const mutation = gql`
      mutation Webshop_scheduleWebshopPublishing($merchantChannelId: ID!) {
        Webshop_scheduleWebshopPublishing(merchantChannelId: $merchantChannelId)
      }
    `;
    return useMutation<Boolean, GQLMutationWebshop_ScheduleWebshopPublishingArgs>(mutation, options);
  }

  merchantChannelSynchronisedWithShopStatusFromResponse(
    data: any
  ): GQLMerchantChannelSynchronisedWithShopStatus {
    if (data === undefined) {
      throw new Exception(`Cannot extract merchant channel stats from undefined input data`);
    }
    if (data.Webshop_getMerchantChannelSynchronisedWithShopStatus !== undefined) {
      return data.Webshop_getMerchantChannelSynchronisedWithShopStatus;
    }
    throw new Exception(`Cannot extract merchant channel stats from expected fields in input data`);
  }

  getMerchantChannelSynchronisedWithShopStatus(
    args: GQLQueryWebshop_GetMerchantChannelSynchronisedWithShopStatusArgs,
    options: QueryHookOptions<
      GQLMerchantChannelSynchronisedWithShopStatus,
      GQLQueryWebshop_GetMerchantChannelSynchronisedWithShopStatusArgs
    > = {}
  ) {
    return useQuery<
      GQLMerchantChannelSynchronisedWithShopStatus,
      GQLQueryWebshop_GetMerchantChannelSynchronisedWithShopStatusArgs
    >(
      gql`
        ${MERCHANT_CHANNEL_SYNCHRONISED_WITH_SHOP_STATUS_FIELDS}
        query getMerchantChannelSynchronisedWithShopStatus($merchantChannelId: ID!) {
          Webshop_getMerchantChannelSynchronisedWithShopStatus(merchantChannelId: $merchantChannelId) {
            ...MerchantChannelSynchronisedWithShopStatusFields
          }
        }
      `,
      {
        ...options,
        variables: args
      }
    );
  }
}
